// src/components/EquipmentManager/EquipmentList.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './EquipmentList.module.css';
import { FaBox } from 'react-icons/fa';
import axios from 'axios';
import EquipmentCard from './EquipmentCard';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useNotification } from '../../context/NotificationContext';
import { SkeletonCard } from '../common/LoadingSkeleton';
import ErrorBoundary from '../common/ErrorBoundary';
import equipmentApi from '../../api/equipmentApi';

const EquipmentList = ({ 
  equipments = [],
  loading = false,
  error = null,
  canManage = false,
  programId, 
  onEditEquipment, 
  onEquipmentClick,
  onAssign,
  onEquipmentDeleted
}) => {
  const { showSuccess, showError } = useNotification();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);

  const handleDeleteEquipment = async () => {
    if (!equipmentToDelete) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/sports-equipments/${equipmentToDelete._id}`
      );
      
      showSuccess(`"${equipmentToDelete.equipmentName}" deleted successfully!`);
      
      // Notify parent to refresh
      if (onEquipmentDeleted) {
        onEquipmentDeleted(equipmentToDelete._id);
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      showError('Failed to delete equipment');
    } finally {
      setShowDeleteConfirm(false);
      setEquipmentToDelete(null);
    }
  };

  const openDeleteConfirm = (equipmentId) => {
    const equipment = equipments.find(eq => eq._id === equipmentId);
    setEquipmentToDelete(equipment);
    setShowDeleteConfirm(true);
  };

  const handleBuyClick = async (equipmentId, linkType) => {
    // Track click
    await equipmentApi.trackClick(equipmentId, linkType);
  };

  if (loading) {
    return (
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <FaBox className={styles.sectionIcon} />
          <h2 className={styles.sectionTitle}>Equipment</h2>
          <span className={styles.equipmentCount}>Loading...</span>
        </div>
        <div className={styles.equipmentGrid}>
          {[1, 2, 3, 4].map(i => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <FaBox className={styles.sectionIcon} />
          <h2 className={styles.sectionTitle}>Equipment</h2>
          <span className={styles.equipmentCount}>Error</span>
        </div>
        <div className={styles.message}>Error: {error}</div>
      </div>
    );
  }

  return (
    <ErrorBoundary errorTitle="Equipment Display Error" errorMessage="Failed to display equipment list">
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <FaBox className={styles.sectionIcon} />
          <h2 className={styles.sectionTitle}>Equipment</h2>
          <span className={styles.equipmentCount}>{equipments.length} items</span>
        </div>

        {equipments.length === 0 ? (
          <div className={styles.emptyState}>
            <FaBox className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>No Equipment Found</h3>
            <p className={styles.emptyText}>No equipment has been added yet. Add your first item to get started!</p>
          </div>
        ) : (
          <div className={styles.equipmentGrid}>
            {equipments.map((equipment, index) => (
              <EquipmentCard
                key={equipment._id}
                equipment={equipment}
                canManage={canManage}
                onView={onEquipmentClick}
                onEdit={onEditEquipment}
                onDelete={openDeleteConfirm}
                onAssign={onAssign}
                onBuyClick={handleBuyClick}
                animationDelay={index * 50}
              />
            ))}
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setEquipmentToDelete(null);
          }}
          onConfirm={handleDeleteEquipment}
          title="Delete Equipment"
          message={equipmentToDelete ? `Are you sure you want to delete "${equipmentToDelete.equipmentName}"? This action cannot be undone.` : ''}
        />
      </div>
    </ErrorBoundary>
  );
};

EquipmentList.propTypes = {
  equipments: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  canManage: PropTypes.bool,
  programId: PropTypes.string.isRequired,
  onEditEquipment: PropTypes.func,
  onEquipmentClick: PropTypes.func,
  onAssign: PropTypes.func,
  onEquipmentDeleted: PropTypes.func
};

export default EquipmentList;
